import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Button, Modal } from '@enamelai/design-system'
import { Icon as ChevronRightIcon } from '@enamelai/design-system/icons/Pagination_chevron_right'
import { SchemaFormRenderer } from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import { formDataToSchemaEntry } from '@components/pages/FormBuilder/SendingFormsTable/formTemplateToFormData'
import type { AppointmentSchedule } from './RemindPatientModal.component'
import { generateFormPDF } from '@utils/formPdfGenerator.util'
import {
  getCompletedFormDownloadUrl,
  regenerateSignedFormPdf,
} from '@services/api/forms.api'
import { useAppSelector } from '@store/hooks'
import { buildClinicPdfHeaderPayload } from '@utils/clinic.util'
import { FormPdfCapture } from '@components/pages/FormBuilder/Shared/FormPdfCapture'
import './CompletedFormsModal.css'

export interface FormData {
  id: string
  name: string
  /** Date the form was sent/assigned (YYYY-MM-DD or display string). */
  dateSent?: string
  /** Date the form was signed/completed (YYYY-MM-DD or display string). */
  dateCompleted?: string
  /** Assignment id (for Azure PDF download). */
  assignmentId?: string
  /** Azure blob name for signed PDF. */
  signedPdfBlobName?: string
  /** Server signed-PDF status: pending, generated, or failed. */
  signedPdfStatus?: string
  signedPdfLastError?: string
  fields: {
    section: string
    items: {
      label: string
      value: string | null
      options?: string[]
      selected?: string
      selectedOptions?: string[]
      checked?: boolean
      isCheckbox?: boolean
      isCheckboxGroup?: boolean
      isDropdown?: boolean
      isTextArea?: boolean
      /** Items sharing the same rowGroup belong to the same Form Row (side-by-side layout). */
      rowGroup?: number
      /** True when the field is a Rich Text area. */
      isRichText?: boolean
      /** True when the field is a Date Field (renders as date input). */
      isDate?: boolean
      /** True when the field is an Attachments field. */
      isAttachments?: boolean
    }[]
  }[]
}

/** Override Personal Information and Contact with selected patient so modal shows the right patient. */
export function mergePatientIntoForms(
  forms: FormData[],
  context: {
    patientName: string
    patientMobile?: string
    patientEmail?: string
  },
): FormData[] {
  const parts = context.patientName.trim().split(/\s+/)
  const first = parts[0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1]! : ''
  const middle = parts.length > 2 ? parts.slice(1, -1).join(' ') : ''

  return forms.map((form) => ({
    ...form,
    fields: form.fields.map((section) => {
      if (section.section === 'Personal Information') {
        return {
          ...section,
          items: section.items.map((item) => {
            if (item.label === 'First name') return { ...item, value: first }
            if (item.label === 'Last name') return { ...item, value: last }
            if (item.label === 'Middle name') return { ...item, value: middle }
            return item
          }),
        }
      }
      if (section.section === 'Contact Information') {
        return {
          ...section,
          items: section.items.map((item) => {
            if (
              item.label === 'Mobile/Telephone' &&
              context.patientMobile != null
            )
              return { ...item, value: context.patientMobile }
            if (item.label === 'Email' && context.patientEmail != null)
              return { ...item, value: context.patientEmail }
            return item
          }),
        }
      }
      return section
    }),
  }))
}

interface CompletedFormsModalProps {
  show: boolean
  onHide: () => void
  patientName: string
  forms: FormData[]
  patientMobile?: string
  patientEmail?: string
  appointments?: AppointmentSchedule[]
}

const CompletedFormsModal: React.FC<CompletedFormsModalProps> = ({
  show,
  onHide,
  patientName,
  forms,
  patientMobile,
  patientEmail,
}) => {
  const formsWithPatient = useMemo(
    () =>
      mergePatientIntoForms(forms, {
        patientName,
        patientMobile,
        patientEmail,
      }),
    [forms, patientName, patientMobile, patientEmail],
  )
  const [selectedFormId, setSelectedFormId] = useState<string | null>(
    forms.length > 0 ? forms[0]!.id : null,
  )
  const selectedForm =
    formsWithPatient.find((f) => f.id === selectedFormId) ?? null
  const selectedSchemaEntry = useMemo(() => {
    if (!selectedForm) return null
    return formDataToSchemaEntry(selectedForm)
  }, [selectedForm])
  const pdfDownloadContainerRef = useRef<HTMLDivElement>(null)
  const [pdfNotice, setPdfNotice] = useState<string>('')
  const [localPdfStatus, setLocalPdfStatus] = useState<string | undefined>(
    undefined,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const clinicPdfHeader = useMemo(
    () => buildClinicPdfHeaderPayload(selectedClinic),
    [selectedClinic],
  )

  const handleClose = (): void => {
    requestAnimationFrame(() => {
      onHide()
    })
  }

  const pdfStatus = localPdfStatus ?? selectedForm?.signedPdfStatus

  const handleRegeneratePdf = (): void => {
    if (!selectedForm?.assignmentId) return
    setPdfNotice('')
    regenerateSignedFormPdf(selectedForm.assignmentId)
      .then(() => {
        setLocalPdfStatus('pending')
        setPdfNotice('Signed PDF processing')
      })
      .catch(() => {
        setPdfNotice('Could not start signed PDF generation.')
      })
  }

  const handleDownloadPdf = (): void => {
    if (!selectedForm) return
    setPdfNotice('')
    if (pdfStatus === 'pending') {
      setPdfNotice('Signed PDF processing')
      return
    }
    if (pdfStatus === 'failed') {
      setPdfNotice(
        selectedForm.signedPdfLastError ||
          'Signed PDF generation failed. Regenerate the PDF.',
      )
      return
    }
    if (selectedForm.assignmentId && selectedForm.signedPdfBlobName) {
      getCompletedFormDownloadUrl(selectedForm.assignmentId)
        .then((res) => {
          if (res.download_url) {
            window.open(res.download_url, '_blank', 'noopener,noreferrer')
          } else {
            setPdfNotice('Signed PDF is not available yet.')
          }
        })
        .catch(() => {
          setPdfNotice('Signed PDF is not available yet.')
        })
      return
    }
    const el = pdfDownloadContainerRef.current
    if (!el) return
    const filename = `${patientName.replace(/\s+/g, '-')}-${selectedForm.name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
    generateFormPDF({
      element: el,
      filename,
      title: selectedForm.name,
      clinicPdfHeader,
    })
  }

  useEffect(() => {
    if (!show && forms.length > 0) {
      setSelectedFormId(forms[0]!.id)
    }
  }, [show, forms])

  useEffect(() => {
    if (!show) return

    const errorHandler = (event: ErrorEvent): boolean | void => {
      if (
        event.message &&
        (event.message.includes(
          'ResizeObserver loop completed with undelivered notifications',
        ) ||
          event.message.includes('ResizeObserver loop limit exceeded'))
      ) {
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    }

    const unhandledRejectionHandler = (event: PromiseRejectionEvent): void => {
      if (
        event.reason &&
        typeof event.reason === 'object' &&
        'message' in event.reason &&
        typeof event.reason.message === 'string' &&
        (event.reason.message.includes(
          'ResizeObserver loop completed with undelivered notifications',
        ) ||
          event.reason.message.includes('ResizeObserver loop limit exceeded'))
      ) {
        event.preventDefault()
      }
    }

    const originalError = console.error
    const consoleErrorHandler = (...args: unknown[]): void => {
      const firstArg = args[0]
      if (
        typeof firstArg === 'string' &&
        (firstArg.includes(
          'ResizeObserver loop completed with undelivered notifications',
        ) ||
          firstArg.includes('ResizeObserver loop limit exceeded'))
      ) {
        return
      }
      if (
        firstArg instanceof Error &&
        (firstArg.message.includes(
          'ResizeObserver loop completed with undelivered notifications',
        ) ||
          firstArg.message.includes('ResizeObserver loop limit exceeded'))
      ) {
        return
      }
      originalError.apply(console, args)
    }

    window.addEventListener('error', errorHandler, true)
    window.addEventListener('unhandledrejection', unhandledRejectionHandler)
    console.error = consoleErrorHandler

    return () => {
      window.removeEventListener('error', errorHandler, true)
      window.removeEventListener(
        'unhandledrejection',
        unhandledRejectionHandler,
      )
      console.error = originalError
    }
  }, [show])

  return (
    <Modal
      open={show}
      onClose={handleClose}
      size="xl"
      title={`${patientName}'s completed forms`}
      className="completed-forms-modal"
      bodyClassName="completed-forms-modal__body"
      secondaryLabel=""
      primaryLabel="Close"
      onPrimaryClick={handleClose}
      footerStart={
        <div className="completed-forms-modal__footer-start">
          {pdfNotice ? (
            <span className="completed-forms-modal__notice">{pdfNotice}</span>
          ) : null}
          {pdfStatus === 'failed' && selectedForm?.assignmentId ? (
            <Button variant="secondary" size="sm" onClick={handleRegeneratePdf}>
              Regenerate PDF
            </Button>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={!selectedForm || pdfStatus === 'pending'}
          >
            {pdfStatus === 'pending'
              ? 'Signed PDF processing'
              : 'Download as PDF'}
          </Button>
        </div>
      }
    >
      <div className="completed-forms-container">
        <div className="completed-forms-left">
          <div className="selected-forms-header">Selected forms</div>
          {formsWithPatient.map((form) => (
            <button
              type="button"
              key={form.id}
              className={`form-item${
                selectedFormId === form.id ? ' active' : ''
              }`}
              aria-pressed={selectedFormId === form.id}
              onClick={() => setSelectedFormId(form.id)}
            >
              <span>{form.name}</span>
              <ChevronRightIcon className="form-item__icon" aria-hidden />
            </button>
          ))}
        </div>
        <div className="completed-forms-right">
          <div className="completed-forms-schema-wrapper">
            {selectedSchemaEntry ? (
              <SchemaFormRenderer
                groups={selectedSchemaEntry.groups}
                formTitle={selectedForm?.name}
                fieldValues={selectedSchemaEntry.fieldValues}
                readOnly={true}
                richTextReadOnly={true}
                readOnlyDisplayMode="print"
              />
            ) : null}
          </div>
        </div>
      </div>
      {selectedSchemaEntry && selectedForm && (
        <div
          ref={pdfDownloadContainerRef}
          style={{
            position: 'fixed',
            left: '-9999px',
            top: 0,
            width: '794px',
            zIndex: -1,
            pointerEvents: 'none',
          }}
          className="form-preview-container"
          aria-hidden="true"
        >
          <FormPdfCapture
            groups={selectedSchemaEntry.groups}
            formTitle={selectedForm.name}
            fieldValues={selectedSchemaEntry.fieldValues}
            readOnlyDisplayMode="print"
          />
        </div>
      )}
    </Modal>
  )
}

export default CompletedFormsModal
